import MaterialPagination from '@mui/material/Pagination';
import queryString from 'query-string';
import React, { ReactElement, useEffect, useState } from 'react';
import { handleApiGlobalError } from '../../helpers/asyncActions';
import useDidMount from '../../hooks/useDidMount';
import { useGlobalContext } from '../../stores/global';
import styles from './Pagination.module.scss';

const INITIAL_PAGE = 1;
const ITEMS_PER_PAGE = 18;

interface Props {
  apiFn: (attrs: {
    page: number;
    limit: number;
    [x: string]: any;
  }) => Promise<any>;
  propsLimit?: Number;
  triggerReload?: boolean;
  onItemsLoaded?: (items: any) => void;
  customQueryParams?: { [x: string]: any };
  render: (
    items: any[] | undefined,
    isLoading: boolean,
    error: any,
    paginationEl?: JSX.Element,
    avgPrices?: any[] | undefined
  ) => ReactElement;
}

export default function Pagination({
  apiFn,
  propsLimit,
  render,
  triggerReload,
  customQueryParams,
  onItemsLoaded,
}: Props) {
  const { dispatch } = useGlobalContext();
  const didMount = useDidMount();
  const [items, setItems] = useState<any[]>();
  const [avgPrices, setAvgPrices] = useState<any[]>();
  const [error, setError] = useState<any>();

  const queryParams = queryString.parse(window.location.search);
  const [page, setPage] = useState(Number(queryParams.page || INITIAL_PAGE));
  const [limit] = useState(
    Number(propsLimit || queryParams.limit || ITEMS_PER_PAGE)
  );
  const [totalPages, setTotalPages] = useState<number>();

  const apiFnCall = (page: number, limit: number) => {
    return apiFn({
      ...queryParams,
      ...customQueryParams,
      page,
      limit,
    })
      .then((response) => {
        const data = response.data.data;
        setAvgPrices(data.avg);
        let items = data?.docs;

        if (typeof items === 'undefined') {
          // TODO: the https://api.hauscanner.com/v1/meeting in
          // http://localhost:8080/appointments is not paginated
          // so this monkeypatch is for that one
          items = data;
        }

        setItems(items);
        setTotalPages(data?.totalPages);
        setPage(data?.page);
        onItemsLoaded?.(items);
      })
      .catch((err) => {
        setError('Ocurrió un error');
        throw err;
      })
      .catch(handleApiGlobalError.bind(null, dispatch));
  };

  useEffect(() => {
    apiFnCall(page, limit);
  }, []);

  useEffect(() => {
    if (didMount) {
      dispatch({ type: 'SET_LOADING', payload: true });
      apiFnCall(page, limit).finally(() =>
        dispatch({ type: 'SET_LOADING', payload: false })
      );
    }
  }, [triggerReload]);

  const handlePageChange = (_: any, page: number) => {
    const queryParamsStr = queryString.stringify({
      ...queryParams,
      page,
      limit,
    });
    window.location.href = `${window.location.pathname}?${queryParamsStr}`;
  };

  const isLoading = !items;
  const paginationEl =
    totalPages && totalPages > 1 ? (
      <MaterialPagination
        hidePrevButton
        hideNextButton
        page={page}
        className={styles.pagination}
        count={totalPages}
        onChange={handlePageChange}
      />
    ) : undefined;

  return render(items, isLoading, error, paginationEl, avgPrices);
}
